<template>
  <div id="app" class="app-content mb-4">
    <app-header></app-header>
    <div class="container">
      <div class="row">
        <div class="col-lg-12 text-center">
          <div class="amej-title"><h1>Payments</h1></div>
        </div>
      </div>

      <!-- FILTERS -->
      <div class="row align-items-end filter-row">
        <div class="col-12 col-md-3 mb-2">
          <label>Direction</label>
          <b-form-select v-model="filters.direction" :options="directionOptions"></b-form-select>
        </div>
        <div class="col-12 col-md-2 mb-2">
          <label>Status</label>
          <b-form-select v-model="filters.status" :options="statusOptions"></b-form-select>
        </div>
        <div class="col-12 col-md-2 mb-2">
          <label>From</label>
          <b-form-input v-model="filters.dateFrom" type="date"></b-form-input>
        </div>
        <div class="col-12 col-md-2 mb-2">
          <label>To</label>
          <b-form-input v-model="filters.dateTo" type="date"></b-form-input>
        </div>
        <div class="col-12 col-md-3 mb-2">
          <label>Search</label>
          <b-form-input v-model="filters.search" type="search" placeholder="name, email, remarks, id"></b-form-input>
        </div>
      </div>
      <div class="row mb-3">
        <div class="col-12">
          <b-button variant="secondary" size="sm" @click="resetFilters">Reset</b-button>
          <b-form-select
            v-model="perPage" :options="perPageOptions" size="sm"
            class="ml-2 per-page-select"
          ></b-form-select>
          <b-button variant="primary" size="sm" class="ml-2" @click="exportCsv">
            <span class="material-icons align-middle ic">download</span> Export CSV
          </b-button>
          <span class="muted ml-3" v-if="filtered.length">
            {{ filtered.length }} of {{ payments.length }} payments
          </span>
        </div>
      </div>

      <!-- LIST -->
      <div class="row">
        <div class="col-12">
          <div class="row m-0 mb-2" v-if="totalRows">
            <small class="muted">
              Showing {{ firstIndex }}-{{ lastIndex }} of {{ totalRows }} payments
            </small>
          </div>
          <b-table
            hover responsive striped bordered outlined show-empty
            :items="pagedRows" :fields="fields" :busy="isLoading"
          >
            <template #table-busy>
              <div class="text-center py-4"><b-spinner label="Loading..."></b-spinner></div>
            </template>
            <template #empty>
              <div class="text-center py-3">No payments found.</div>
            </template>
            <template #cell(index)="data">{{ data.index + 1 }}.</template>
            <template #cell(party)="data">
              <div class="party">
                <img class="avatar-sm" v-if="partyOf(data.item).image" :src="partyOf(data.item).image" />
                <div>
                  <div>{{ partyOf(data.item).full_name || '-' }}</div>
                  <small class="muted">{{ partyOf(data.item).email || '' }}</small>
                </div>
              </div>
            </template>
            <template #cell(direction)="data">
              <span class="status-pill" :class="data.item.direction === 'credit' ? 'is-success' : 'is-debit'">
                {{ data.item.direction === 'credit' ? 'Received' : 'Sent' }}
              </span>
            </template>
            <template #cell(amount)="data">
              <strong :class="data.item.direction === 'credit' ? 'amt-in' : 'amt-out'">
                {{ data.item.direction === 'credit' ? '+' : '-' }} $ {{ money(data.item.amount) }}
              </strong>
            </template>
            <template #cell(status)="data">
              <span class="status-pill" :class="data.item.status ? 'is-success' : 'is-failed'">
                {{ data.item.status ? 'Success' : 'Failed' }}
              </span>
            </template>
            <template #cell(date)="data">{{ formatDate(data.item.date) }}</template>
            <template #cell(actions)="data">
              <a href="#" class="btn btn-primary btn-sm" @click.prevent="openDetail(data.item)">Details</a>
            </template>
          </b-table>

          <nav v-if="totalPages > 1">
            <ul class="pagination">
              <li class="page-item" :class="page <= 1 ? 'disabled' : ''">
                <a href="#" class="page-link" @click.prevent="goToPage(page - 1)">&laquo; Previous</a>
              </li>
              <li class="page-item" v-if="pageWindow.length && pageWindow[0] > 1">
                <a href="#" class="page-link" @click.prevent="goToPage(1)">1</a>
              </li>
              <li class="page-item disabled" v-if="pageWindow.length && pageWindow[0] > 2">
                <span class="page-link">...</span>
              </li>
              <li class="page-item" v-for="i in pageWindow" :key="i" :class="page === i ? 'active' : ''">
                <a href="#" class="page-link" @click.prevent="goToPage(i)">{{ i }}</a>
              </li>
              <li class="page-item disabled" v-if="pageWindow.length && pageWindow[pageWindow.length-1] < totalPages - 1">
                <span class="page-link">...</span>
              </li>
              <li class="page-item" v-if="pageWindow.length && pageWindow[pageWindow.length-1] < totalPages">
                <a href="#" class="page-link" @click.prevent="goToPage(totalPages)">{{ totalPages }}</a>
              </li>
              <li class="page-item" :class="page >= totalPages ? 'disabled' : ''">
                <a href="#" class="page-link" @click.prevent="goToPage(page + 1)">Next &raquo;</a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <!-- DETAIL -->
    <b-modal id="txn-detail-modal" centered size="lg" title="Payment Details">
      <div id="txn-invoice" class="invoice" v-if="detail">
        <div class="inv-head">
          <img :src="logoSrc" class="inv-logo" alt="Amej Pay" />
          <div class="inv-title">Payment Receipt</div>
        </div>
        <div class="inv-amount">
          {{ detail.direction === 'credit' ? '+' : '-' }} $ {{ money(detail.amount) }}
        </div>
        <div class="inv-status" :class="detail.status ? '' : 'is-fail'">
          {{ detail.status ? 'Transaction successful' : 'Transaction failed' }}
        </div>
        <div class="inv-rows">
          <div class="detail-row" v-for="(f, i) in detailRows" :key="'d' + i">
            <span>{{ f.label }}</span><strong>{{ f.value || '-' }}</strong>
          </div>
        </div>
      </div>
      <template #modal-footer>
        <b-button size="sm" variant="primary" @click="downloadDetailPdf">
          <span class="material-icons align-middle ic">picture_as_pdf</span> PDF
        </b-button>
        <b-button size="sm" variant="primary" @click="printDetail">
          <span class="material-icons align-middle ic">print</span> Print
        </b-button>
        <b-button size="sm" variant="warning" @click="openIssue">
          <span class="material-icons align-middle ic">report_problem</span> Report Issue
        </b-button>
        <b-button size="sm" variant="secondary" @click="$bvModal.hide('txn-detail-modal')">Close</b-button>
      </template>
    </b-modal>

    <!-- ISSUE FORM -->
    <b-modal id="txn-issue-modal" centered title="Report a Payment Issue">
      <p class="note" v-if="detail">
        Raising an issue for transaction <strong>{{ detail.id }}</strong>
        ($ {{ money(detail.amount) }}).
      </p>
      <div class="form-row-x">
        <label>Issue Subject *</label>
        <b-form-select v-model="issue.subject" :options="issueSubjects"></b-form-select>
        <span class="text-danger" v-if="issueErrors.subject">{{ issueErrors.subject }}</span>
      </div>
      <div class="form-row-x">
        <label>Issue Description *</label>
        <b-form-textarea v-model="issue.description" rows="4"
          placeholder="Explain what went wrong"></b-form-textarea>
        <span class="text-danger" v-if="issueErrors.description">{{ issueErrors.description }}</span>
      </div>
      <!-- An unavailable endpoint is information, not a failure the person caused,
           so it reads as a notice with a working alternative rather than a red error. -->
      <div class="issue-alert" v-if="issueError" :class="issueUnavailable ? 'is-info' : 'is-error'">
        <span class="material-icons">{{ issueUnavailable ? 'info' : 'error_outline' }}</span>
        <div>
          <div>{{ issueError }}</div>
          <a v-if="issueUnavailable" class="issue-mail" :href="issueMailto">
            Email support about this transaction
          </a>
        </div>
      </div>
      <template #modal-footer="{ cancel }">
        <b-button size="sm" variant="success" :disabled="isSubmittingIssue" @click="submitIssue">
          <b-spinner small v-if="isSubmittingIssue" class="mr-1"></b-spinner>
          {{ isSubmittingIssue ? "Submitting..." : "Submit Issue" }}
        </b-button>
        <b-button size="sm" variant="secondary" @click="cancel()">Cancel</b-button>
      </template>
    </b-modal>

    <b-toast id="txn-toast" variant="success" solid append-toast toaster="b-toaster-top-center">
      <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
        <strong class="mr-auto">Amej Pay</strong>
      </div>
      <div><p>{{ infoText }}</p></div>
    </b-toast>
  </div>
</template>

<script>
import AppHeader from "../reuseable_components/Header.vue";
import { mapActions, mapState } from "vuex";
import { jsPDF } from "jspdf";
import logoUrl from "@/assets/amej-pay.png";

export default {
  name: "Transactions",
  components: { "app-header": AppHeader },
  data() {
    return {
      isLoading: false,
      isSubmittingIssue: false,
      issueUnavailable: false,
      infoText: "",
      issueError: "",
      detail: null,
      logoSrc: logoUrl,
      filters: { direction: "", status: "", dateFrom: "", dateTo: "", search: "" },
      page: 1,
      perPage: 20,
      serverPaginated: false,
      serverCount: 0,
      perPageOptions: [
        { value: 20, text: "20 / page" },
        { value: 50, text: "50 / page" },
        { value: 100, text: "100 / page" },
      ],
      issue: { subject: "", description: "" },
      issueErrors: {},
      directionOptions: [
        { value: "", text: "All" },
        { value: "debit", text: "Sent" },
        { value: "credit", text: "Received" },
      ],
      statusOptions: [
        { value: "", text: "All" },
        { value: "success", text: "Success" },
        { value: "failed", text: "Failed" },
      ],
      issueSubjects: [
        { value: "", text: "Select a subject" },
        { value: "mistake_merchant_payment", text: "Mistake Merchant Payment" },
        { value: "mistake_send_money", text: "Mistake Send Money" },
        { value: "mistake_topup", text: "Mistake Topup" },
        { value: "double_payment", text: "Double Payment" },
        { value: "other", text: "Other" },
      ],
      fields: [
        { key: "index", label: "S.N." },
        { key: "title", label: "Title" },
        { key: "party", label: "From / To" },
        { key: "direction", label: "Type" },
        { key: "amount", label: "Amount" },
        { key: "status", label: "Status" },
        { key: "date", label: "Date" },
        { key: "actions", label: "" },
      ],
    };
  },
  computed: {
    ...mapState(["userPayments", "profile"]),
    payments: function () {
      return Array.isArray(this.userPayments) ? this.userPayments : [];
    },
    filtered: function () {
      const f = this.filters;
      const q = String(f.search || "").toLowerCase().trim();
      const vm = this;
      return this.payments.filter(function (p) {
        if (f.direction && p.direction !== f.direction) { return false; }
        if (f.status === "success" && !p.status) { return false; }
        if (f.status === "failed" && p.status) { return false; }
        if (f.dateFrom && String(p.date || "").slice(0, 10) < f.dateFrom) { return false; }
        if (f.dateTo && String(p.date || "").slice(0, 10) > f.dateTo) { return false; }
        if (q) {
          const party = vm.partyOf(p);
          const hay = [
            p.id, p.title, p.remarks, p.amount,
            party.full_name, party.email,
          ].join(" ").toLowerCase();
          if (hay.indexOf(q) === -1) { return false; }
        }
        return true;
      });
    },
    totalRows: function () {
      return this.serverPaginated ? this.serverCount : this.filtered.length;
    },
    totalPages: function () {
      return Math.max(1, Math.ceil(this.totalRows / this.perPage));
    },
    /**
     * When the API paginates we render exactly what it returned.
     * Otherwise we slice locally so the DOM only ever holds one page,
     * which is what keeps the page responsive with thousands of rows.
     */
    pagedRows: function () {
      if (this.serverPaginated) { return this.filtered; }
      const start = (this.page - 1) * this.perPage;
      return this.filtered.slice(start, start + this.perPage);
    },
    firstIndex: function () {
      return this.totalRows ? (this.page - 1) * this.perPage + 1 : 0;
    },
    lastIndex: function () {
      return Math.min(this.page * this.perPage, this.totalRows);
    },
    pageWindow: function () {
      const total = this.totalPages, cur = this.page;
      let start = Math.max(1, cur - 2);
      let end = Math.min(total, start + 4);
      start = Math.max(1, end - 4);
      const out = [];
      for (let i = start; i <= end; i++) { out.push(i); }
      return out;
    },
    detailRows: function () {
      const d = this.detail;
      if (!d) { return []; }
      const party = this.partyOf(d);
      return [
        { label: "Transaction ID", value: d.id },
        { label: "Title", value: d.title },
        { label: "Type", value: d.direction === "credit" ? "Received" : "Sent" },
        { label: d.direction === "credit" ? "Received From" : "Paid To", value: party.full_name },
        { label: "Email", value: party.email },
        { label: "Remarks", value: d.remarks },
        { label: "Status", value: d.status ? "Success" : "Failed" },
        { label: "Date", value: this.formatDate(d.date) },
      ];
    },
  },
  created() {
    this.load();
  },
  watch: {
    filters: {
      handler: function () { this.page = 1; },
      deep: true,
    },
    perPage: function () {
      this.page = 1;
      if (this.serverPaginated) { this.load(); }
    },
  },
  methods: {
    ...mapActions(["listUserPayments", "submitPaymentIssue"]),
    load() {
      this.isLoading = true;
      const vm = this;
      return this.listUserPayments({
        limit: this.perPage,
        offset: (this.page - 1) * this.perPage,
      })
        .then(function (res) {
          if (res) {
            vm.serverPaginated = !!res.serverPaginated;
            vm.serverCount = res.count || 0;
          }
          vm.isLoading = false;
        })
        .catch(function () { vm.isLoading = false; });
    },
    goToPage(p) {
      if (p < 1 || p > this.totalPages || p === this.page) { return; }
      this.page = p;
      // Only refetch when the server is doing the paging.
      if (this.serverPaginated) { this.load(); }
      window.scrollTo(0, 0);
    },
    /** credit -> the other party is the author; debit -> it is the recipient. */
    partyOf(p) {
      if (!p) { return {}; }
      return (p.direction === "credit" ? (p.author || {}) : (p.to || {}));
    },
    money(v) { const n = Number(v); return (isNaN(n) ? 0 : n).toFixed(2); },
    formatDate(value) {
      if (!value) { return ""; }
      const d = new Date(value);
      if (isNaN(d.getTime())) { return String(value); }
      return d.toLocaleString("en-GB", {
        day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    },
    resetFilters() {
      this.filters = { direction: "", status: "", dateFrom: "", dateTo: "", search: "" };
    },
    exportCsv() {
      // Exports every filtered row, not only the page on screen.
      const rows = this.filtered;
      if (!rows.length) {
        this.infoText = "There is nothing to export.";
        this.$bvToast.show("txn-toast");
        return;
      }
      const vm = this;
      const head = ["Transaction ID", "Title", "Type", "Name", "Email", "Amount", "Status", "Remarks", "Date"];
      const esc = function (v) {
        const s = (v === null || v === undefined) ? "" : String(v);
        return '"' + s.replace(/"/g, '""') + '"';
      };
      const lines = [head.map(esc).join(",")];
      rows.forEach(function (p) {
        const party = vm.partyOf(p);
        lines.push([
          p.id, p.title, p.direction === "credit" ? "Received" : "Sent",
          party.full_name, party.email, vm.money(p.amount),
          p.status ? "Success" : "Failed", p.remarks, p.date,
        ].map(esc).join(","));
      });
      const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.setAttribute("download", "amejpay-payments-" + new Date().toISOString().slice(0, 10) + ".csv");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(function () { window.URL.revokeObjectURL(url); }, 1000);
    },
    openDetail(item) {
      this.detail = item;
      this.$bvModal.show("txn-detail-modal");
    },
    loadLogo() {
      const src = this.logoSrc;
      return new Promise(function (resolve) {
        try {
          const img = new Image();
          img.onload = function () { resolve(img); };
          img.onerror = function () { resolve(null); };
          img.src = src;
        } catch (e) { resolve(null); }
      });
    },
    downloadDetailPdf() {
      const vm = this;
      if (!this.detail) { return; }
      return this.loadLogo().then(function (logo) {
        try {
          const doc = new jsPDF("p", "pt", "a4");
          const pageW = doc.internal.pageSize.getWidth();
          const left = 48, right = pageW - 48;
          let y = 60;
          if (logo) {
            try { doc.addImage(logo, "PNG", left, 36, 74, Math.round(74 * (106 / 198))); } catch (e) { /* no logo */ }
          }
          doc.setFontSize(18); doc.setTextColor(20, 20, 20);
          doc.text("Payment Receipt", right, y, { align: "right" });
          y += 52;
          doc.setFontSize(22); doc.setTextColor(0, 102, 179);
          doc.text((vm.detail.direction === "credit" ? "+ $ " : "- $ ") + vm.money(vm.detail.amount), left, y);
          doc.setFontSize(11);
          doc.setTextColor(vm.detail.status ? 0 : 220, vm.detail.status ? 168 : 53, vm.detail.status ? 0 : 69);
          doc.text(vm.detail.status ? "Successful" : "Failed", right, y, { align: "right" });
          y += 14;
          doc.setDrawColor(220, 220, 220); doc.line(left, y, right, y);
          const rows = vm.detailRows;
          for (let i = 0; i < rows.length; i++) {
            if (!rows[i].value) { continue; }
            y += 26;
            if (y > 780) { doc.addPage(); y = 60; }
            doc.setFontSize(10); doc.setTextColor(110, 110, 110);
            doc.text(String(rows[i].label), left, y);
            doc.setTextColor(25, 25, 25);
            const lines = doc.splitTextToSize(String(rows[i].value), 300);
            doc.text(lines, right, y, { align: "right" });
            if (lines.length > 1) { y += (lines.length - 1) * 12; }
          }
          y = Math.min(y + 40, 800);
          doc.setFontSize(9); doc.setTextColor(150, 150, 150);
          doc.text("Generated on " + new Date().toLocaleString("en-GB") + " - Amej Pay", left, y);
          doc.save("payment-" + String(vm.detail.id || "receipt").replace(/[^A-Za-z0-9_-]/g, "") + ".pdf");
        } catch (e) {
          vm.infoText = "Could not generate the PDF.";
          vm.$bvToast.show("txn-toast");
        }
      });
    },
    printDetail() {
      const node = document.getElementById("txn-invoice");
      if (!node) { return; }
      const win = window.open("", "_blank", "width=820,height=900");
      if (!win) { return; }
      win.document.write(
        '<html><head><title>Payment Receipt</title><style>' +
        'body{font-family:Poppins,Arial,sans-serif;padding:32px;color:#1b1b1b;}' +
        '.inv-logo{height:40px;}.inv-title{font-size:20px;font-weight:700;margin-top:8px;}' +
        '.inv-amount{font-size:28px;font-weight:700;color:#0066b3;margin:12px 0 4px;}' +
        '.inv-status{color:#00a800;margin-bottom:16px;}.inv-status.is-fail{color:#dc3545;}' +
        '.detail-row{display:flex;justify-content:space-between;padding:8px 0;border-bottom:1px solid #eee;}' +
        '.detail-row span{color:#6c757d;}' +
        '</style></head><body>' + node.innerHTML + '</body></html>'
      );
      win.document.close();
      win.focus();
      setTimeout(function () { win.print(); }, 350);
    },
    openIssue() {
      this.issue = { subject: "", description: "" };
      this.issueErrors = {};
      this.issueError = "";
      this.$bvModal.show("txn-issue-modal");
    },
    submitIssue() {
      const e = {};
      if (!this.issue.subject) { e.subject = "Please choose a subject."; }
      if (!this.issue.description) { e.description = "Please describe the issue."; }
      this.issueErrors = e;
      this.issueError = "";
      this.issueUnavailable = false;
      if (Object.keys(e).length) { return; }
      this.isSubmittingIssue = true;
      const vm = this;
      return this.submitPaymentIssue({
        transaction_id: this.detail ? this.detail.id : "",
        subject: this.issue.subject,
        description: this.issue.description,
      }).then(function (res) {
        if (res && res.ok) {
          vm.infoText = res.message || "Your issue has been submitted.";
          vm.$bvModal.hide("txn-issue-modal");
          vm.$bvToast.show("txn-toast");
        } else {
          vm.issueError = (res && res.message) || "Unable to submit this issue.";
          // The endpoint is not built yet - offer the route that does work rather
          // than leaving the person staring at a dead form.
          vm.issueUnavailable = Boolean(res && res.unavailable);
        }
      }).then(function () { vm.isSubmittingIssue = false; })
        .catch(function () {
          vm.isSubmittingIssue = false;
          vm.issueError = "Unable to submit this issue.";
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../css/style.scss';

.filter-row label { font-size: 13px; color: #6c757d; margin-bottom: 4px; }
.muted { color: #6c757d; font-size: 13px; }
.note { font-size: 13px; color: #6c757d; }
.form-row-x { margin-bottom: 14px; }
.form-row-x label { display: block; font-size: 14px; color: #6c757d; margin-bottom: 6px; }
.text-danger { color: #dc3545; font-size: 12px; display: block; margin-top: 4px; }
.issue-alert {
  display: flex;
  align-items: flex-start;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.5;
  margin-top: 10px;
}
.issue-alert .material-icons { font-size: 18px; margin-right: 8px; flex: 0 0 auto; }
.issue-alert.is-error { background: #fdeaea; border: 1px solid #f5c2c7; color: #b02a37; }
.issue-alert.is-info  { background: #fff8e6; border: 1px solid #ffe2a8; color: #7a5b12; }
.issue-mail { display: inline-block; margin-top: 6px; font-weight: 600; }
.party { display: flex; align-items: center; gap: 8px; }
.avatar-sm { width: 32px; height: 32px; border-radius: 50%; object-fit: cover; }
.status-pill { font-size: 11px; padding: 2px 9px; border-radius: 10px; font-weight: 600; white-space: nowrap; }
.status-pill.is-success { background: #e3f7e3; color: #00a800; }
.status-pill.is-failed { background: #fde3e5; color: #dc3545; }
.status-pill.is-debit { background: #eef1f4; color: #55606a; }
.amt-in { color: #00a800; }
.amt-out { color: #1b1b1b; }
.ic { font-size: 16px; margin-right: 3px; }
.invoice { padding: 6px 2px; }
.inv-head { display: flex; align-items: center; justify-content: space-between; }
.inv-logo { height: 38px; }
.inv-title { font-size: 18px; font-weight: 700; }
.inv-amount { font-size: 30px; font-weight: 700; color: #0066b3; margin-top: 14px; }
.inv-status { color: #00a800; font-weight: 600; margin-bottom: 14px; }
.inv-status.is-fail { color: #dc3545; }
.detail-row {
  display: flex; justify-content: space-between; padding: 8px 2px;
  border-bottom: 1px solid #f2f4f6; font-size: 14px; gap: 16px;
}
.detail-row:last-child { border-bottom: none; }
.detail-row span { color: #6c757d; }
.detail-row strong { text-align: right; word-break: break-word; }

.per-page-select { width: auto; display: inline-block; vertical-align: middle; }
.pagination .page-item.disabled .page-link { pointer-events: none; opacity: 0.5; }
</style>
